import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { TFunction } from 'i18next';
import styled from 'styled-components';
import { ROUTER_BACKOFFICE } from 'app/AppRouter';
import PrivateComponent from 'composers/PrivateComponent';
import { getUserRights, UserRole } from 'domains/user';
import AdjustmentNumber from 'pages/BackOfficePage/BackOfficeCategories/Promotion/PromotionNotification/AdjustmentNumber';
import {
  ASSISTANCE_CENTRAL,
  ASSISTANCE_COUNTRY,
  BAM_COMMERCIAL_FAMILY_CODES,
  COUNTRY_NETWORK,
  CREATE_NEW,
  CROSS_SELLING,
  DELIVERY_LEAD_TIME,
  DISCONTINUED_REFERENCES,
  EDIT,
  ELECTRONIC_PARTS_REPAIR,
  INFORMATION,
  LANDING_PAGE_CONFIG,
  LIBRARY,
  NOTIFICATION_CENTER,
  PROMOTION,
  R1_NETWORK,
  CENTRAL_SETTINGS,
  SEARCH_RATE_LIMIT,
  USER_PROFILE,
} from 'pages/BackOfficePage/BackOfficePage.type';
import { Flex, Link, MarginBox, Text, Layout } from 'UI';
import { Country } from 'utils/i18n/Country';
import { MenuItemFlex } from './BackOfficeLayout.styled';

const { Sider: AntSider } = Layout;

const SSider = styled(AntSider)`
  background-color: ${({ theme }) => theme.color.white};
`;

export const ELECTRONIC_PARTS_REPAIR_ALLOW_COUNTRIES = [
  Country.FR,
  Country.BE,
  Country.ES,
  Country.CH,
  Country.IT,
  Country.DE,
  Country.GB,
  Country.PL,
  Country.NL,
  Country.AT,
];

export interface BackOfficeMenuItem {
  label: string;
  labelTail?: ReactNode;
  link: string;
  requiredCountries?: Country[];
  requiredRights?: UserRole[];
  'data-cy'?: string;
  subPages?: {
    label: string;
    link: string;
  }[];
}

export const getBackOfficeMenuItems = (userRights: UserRole[] | 'loading', t: TFunction): BackOfficeMenuItem[] => {
  const menuItems = [
    {
      label: t('common.user_profile', 'User profile'),
      link: USER_PROFILE,
      requiredRights: [UserRole.COUNTRY_ADMIN, UserRole.CENTRAL_ADMIN],
      'data-cy': 'user-profile-link',
    },
    {
      label: t('backoffice.landing_page_cover.menu_label', 'Landing page'),
      link: LANDING_PAGE_CONFIG,
      requiredRights: [UserRole.CENTRAL_ADMIN],
      'data-cy': 'landing-page-link',
    },
    {
      label: t('backoffice.menu.my_network', 'My Network'),
      link: R1_NETWORK,
      requiredRights: [UserRole.R1],
      'data-cy': 'my-network-link',
    },
    {
      label: t('backoffice.menu.country_network', 'My Network'),
      link: COUNTRY_NETWORK,
      requiredRights: [UserRole.COUNTRY_ADMIN],
      'data-cy': 'country-network-link',
    },
    {
      label: t('backoffice.menu.discontinued_references', 'Discontinued references'),
      link: DISCONTINUED_REFERENCES,
      requiredRights: [UserRole.COUNTRY_ADMIN],
      'data-cy': 'discontinued-references-link',
    },
    {
      label: t('backoffice.menu.bam_commercial_family_codes', 'BAM commercial family codes'),
      link: BAM_COMMERCIAL_FAMILY_CODES,
      requiredRights: [UserRole.COUNTRY_ADMIN],
      'data-cy': 'bam-commercial-family-codes-link',
    },
    {
      label: t('backoffice.menu.promotion', 'Promotion'),
      labelTail: <AdjustmentNumber />,
      link: PROMOTION,
      requiredRights: [UserRole.COUNTRY_ADMIN, UserRole.PROMOTION],
      'data-cy': 'promotion-link',
      subPages: [
        {
          label: t('backoffice.create_promotion', 'Create promotion'),
          link: CREATE_NEW,
        },
        {
          label: t('backoffice.edit_promotion', 'Edit promotion'),
          link: EDIT,
        },
      ],
    },
    {
      label: t('backoffice.cross_selling', 'Cross selling'),
      link: CROSS_SELLING,
      requiredRights: [UserRole.COUNTRY_ADMIN],
      'data-cy': 'cross-selling-link',
    },
    {
      label: t('backoffice.delivery_lead_time', 'Delivery lead time'),
      requiredRights: [UserRole.COUNTRY_ADMIN],
      link: DELIVERY_LEAD_TIME,
      'data-cy': 'delivery-lead-time-link',
    },
    {
      label: t('backoffice.assistance', 'Assistance'),
      requiredRights: [UserRole.COUNTRY_ADMIN],
      link: ASSISTANCE_COUNTRY,
      'data-cy': 'assistance-country-link',
    },
    {
      label: t('backoffice.assistance', 'Assistance'),
      requiredRights: [UserRole.CENTRAL_ADMIN],
      link: ASSISTANCE_CENTRAL,
      'data-cy': 'assistance-central-link',
    },
    {
      label: t('backoffice.electronic_parts_repair', 'Electronic parts repair'),
      link: ELECTRONIC_PARTS_REPAIR,
      requiredCountries: ELECTRONIC_PARTS_REPAIR_ALLOW_COUNTRIES,
      requiredRights: [UserRole.COUNTRY_ADMIN],
      'data-cy': 'electronic-parts-repair-link',
    },
    {
      label: t('backoffice.library', 'Library'),
      link: LIBRARY,
      requiredRights: [UserRole.COUNTRY_ADMIN, UserRole.CENTRAL_ADMIN],
      'data-cy': 'library-link',
    },
    {
      label: t('backoffice.information', 'Information'),
      link: INFORMATION,
      requiredRights: [UserRole.COUNTRY_ADMIN, UserRole.CENTRAL_ADMIN],
      'data-cy': 'information-link',
      subPages: [
        {
          label: t('backoffice.information.create_information', 'Create information'),
          link: CREATE_NEW,
        },
        {
          label: t('backoffice.information.edit_information', 'Edit information'),
          link: EDIT,
        },
      ],
    },
    {
      label: t('backoffice.notification_center', 'Notification center'),
      link: NOTIFICATION_CENTER,
      requiredRights: [UserRole.COUNTRY_ADMIN, UserRole.CENTRAL_ADMIN],
      'data-cy': 'notification-center-link',
    },
    {
      label: t('backoffice.central-settings', 'Settings'),
      link: CENTRAL_SETTINGS,
      requiredRights: [UserRole.CENTRAL_ADMIN],
      'data-cy': 'settings-link',
    },
    {
      label: t('backoffice.menu.search_rate_limit', 'Search Rate Limit'),
      link: SEARCH_RATE_LIMIT,
      requiredRights: [UserRole.CENTRAL_ADMIN],
      'data-cy': 'search-rate-limit-link',
    },
  ];
  return menuItems.filter((item) => item.requiredRights.some((right) => userRights.includes(right)));
};

const BackOfficeMenu = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const userRights = useSelector(getUserRights);
  const backOfficeMenuItems = getBackOfficeMenuItems(userRights, t);

  const isActive = (link: string) => history.location.pathname.includes(link);

  return (
    <MarginBox ml={18} mt={90}>
      <SSider width={300}>
        <Flex direction={'column'}>
          {backOfficeMenuItems.map((menuItem) => {
            return (
              <PrivateComponent
                key={menuItem.link}
                render={() => (
                  <Link to={`${ROUTER_BACKOFFICE}/${menuItem.link}`}>
                    <MenuItemFlex
                      minHeight={50}
                      align={'center'}
                      $isActive={isActive(menuItem.link)}
                      data-cy={menuItem['data-cy']}
                    >
                      <MarginBox ml={30}>
                        <Text type="base" cursor={'pointer'} disableGutter>
                          {menuItem.label}
                        </Text>
                      </MarginBox>
                      {menuItem.labelTail && (
                        <Flex direction={'row-reverse'}>
                          <MarginBox mr={15}>{menuItem.labelTail}</MarginBox>
                        </Flex>
                      )}
                    </MenuItemFlex>
                  </Link>
                )}
                requiredRights={menuItem.requiredRights ?? []}
                requiredCountries={menuItem.requiredCountries ?? []}
              />
            );
          })}
        </Flex>
      </SSider>
    </MarginBox>
  );
};

export default BackOfficeMenu;
